import { useCallback, useMemo, useState } from "react";
import {
  Button,
  Flex,
  Icon,
  Icons,
  Paragraph,
  type TableColumnType,
} from "@ir/ui";
import {
  ConfigPortalCredential,
  type IConfigPortalCredential,
} from "@src/domain/entities/config";
import { Permissions } from "@src/domain/permissions";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import {
  CONFIG_COLUMN_VERTICAL_ALIGN,
  ConfigListTable,
  renderConfigStatusChip,
} from "./configTable.shared";

export interface ConfigPortalCredentialRow extends IConfigPortalCredential {
  key: string;
}

function mapToRows(
  credentials: ConfigPortalCredential[],
): ConfigPortalCredentialRow[] {
  return credentials.map((entity) => ({
    ...ConfigPortalCredential.toJSON(entity),
    key: String(entity.id),
  }));
}

function hasMetadata(value: unknown): boolean {
  if (value == null || value === "") return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    return Object.keys(value as object).length > 0;
  }
  return true;
}

function buildColumns(
  onEdit: (credential: ConfigPortalCredential) => void,
  onDelete: (credential: ConfigPortalCredential) => void,
  onViewMetadata: (credential: ConfigPortalCredential) => void,
  revealedPasswordIds: ReadonlySet<number>,
  onTogglePassword: (credentialId: number) => void,
  byId: Map<number, ConfigPortalCredential>,
): TableColumnType<ConfigPortalCredentialRow>[] {
  return [
    {
      title: "Purchaser Name",
      dataIndex: "purchaserName",
      key: "purchaserName",
      align: "left",
      verticalAlign: CONFIG_COLUMN_VERTICAL_ALIGN,
      width: 220,
      fixed: "left",
    },
    {
      title: "Portal User Name",
      dataIndex: "portalUserName",
      key: "portalUserName",
      align: "left",
      verticalAlign: CONFIG_COLUMN_VERTICAL_ALIGN,
      width: 220,
    },
    {
      title: "Portal Password",
      dataIndex: "portalPassword",
      key: "portalPassword",
      align: "left",
      verticalAlign: CONFIG_COLUMN_VERTICAL_ALIGN,
      width: 200,
      cellRender: (_value: unknown, record: ConfigPortalCredentialRow) => {
        const credential = byId.get(record.id);
        if (!credential) return null;
        const password = credential.portalPassword?.trim() ?? "";
        if (!password) {
          return <Paragraph className="m-0">—</Paragraph>;
        }
        const isRevealed = revealedPasswordIds.has(credential.id);
        return (
          <Flex align="center" gap={0.25} justifyContent="start">
            <Paragraph className="m-0 break-all">
              {isRevealed ? password : "••••••••••••••••"}
            </Paragraph>
            <Button
              size="small"
              variant="clear"
              color="grey"
              aria-label={
                isRevealed
                  ? `Hide portal password for ${credential.purchaserName}`
                  : `Show portal password for ${credential.purchaserName}`
              }
              onPress={() => onTogglePassword(credential.id)}
            >
              <Icon name={Icons.Eye} size="small" />
            </Button>
          </Flex>
        );
      },
    },
    {
      title: "Portal Key",
      dataIndex: "portalKey",
      key: "portalKey",
      align: "left",
      verticalAlign: CONFIG_COLUMN_VERTICAL_ALIGN,
      width: 200,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      align: "left",
      verticalAlign: CONFIG_COLUMN_VERTICAL_ALIGN,
      width: 120,
      cellRender: (value: unknown) => renderConfigStatusChip(value),
    },
    {
      title: "Metadata",
      dataIndex: "metadata",
      key: "metadata",
      align: "left",
      verticalAlign: CONFIG_COLUMN_VERTICAL_ALIGN,
      width: 110,
      cellRender: (_value: unknown, record: ConfigPortalCredentialRow) => {
        const credential = byId.get(record.id);
        if (!credential) return null;
        if (!hasMetadata(credential.metadata)) {
          return <Paragraph className="m-0 text-grey-500">—</Paragraph>;
        }
        return (
          <Flex align="center" justifyContent="start">
            <Button
              size="small"
              color="primary"
              aria-label={`View metadata for ${credential.purchaserName}`}
              onPress={() => onViewMetadata(credential)}
              style={{ padding: "2px 8px", fontSize: "12px", letterSpacing: "1px" }}
            >
              View
            </Button>
          </Flex>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "id",
      key: "action",
      align: "left",
      verticalAlign: CONFIG_COLUMN_VERTICAL_ALIGN,
      width: 100,
      fixed: "right",
      cellRender: (_value: unknown, record: ConfigPortalCredentialRow) => {
        const credential = byId.get(record.id);
        if (!credential) return null;
        return (
          <Flex align="center" justifyContent="start" gap={0.25}>
            <HasAnyPermission requiredPermissions={[Permissions.CONFIG_EDIT]}>
              <Button
                size="small"
                variant="clear"
                color="grey"
                aria-label={`Edit portal credential for ${credential.purchaserName}`}
                onPress={() => onEdit(credential)}
              >
                <Icon name={Icons.Edit} size="small" />
              </Button>
            </HasAnyPermission>
            {!credential.isActive ? (
              <HasAnyPermission
                requiredPermissions={[Permissions.CONFIG_DELETE]}
              >
                <Button
                  size="small"
                  variant="clear"
                  color="grey"
                  aria-label={`Delete portal credential for ${credential.purchaserName}`}
                  onPress={() => onDelete(credential)}
                >
                  <Icon name={Icons.Trash} size="small" />
                </Button>
              </HasAnyPermission>
            ) : null}
          </Flex>
        );
      },
    },
  ];
}

interface ConfigPortalCredentialsTableProps {
  credentials: ConfigPortalCredential[];
  search: string;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
  onEdit: (credential: ConfigPortalCredential) => void;
  onDelete: (credential: ConfigPortalCredential) => void;
  onViewMetadata: (credential: ConfigPortalCredential) => void;
}

export const ConfigPortalCredentialsTable = ({
  credentials,
  search,
  setSearch,
  setPage,
  onEdit,
  onDelete,
  onViewMetadata,
}: ConfigPortalCredentialsTableProps) => {
  const [revealedPasswordIds, setRevealedPasswordIds] = useState<Set<number>>(
    () => new Set(),
  );

  const handleTogglePassword = useCallback((credentialId: number) => {
    setRevealedPasswordIds((prev) => {
      const next = new Set(prev);
      if (next.has(credentialId)) {
        next.delete(credentialId);
      } else {
        next.add(credentialId);
      }
      return next;
    });
  }, []);

  const byId = useMemo(
    () => new Map(credentials.map((item) => [item.id, item])),
    [credentials],
  );
  const columns = useMemo(
    () =>
      buildColumns(
        onEdit,
        onDelete,
        onViewMetadata,
        revealedPasswordIds,
        handleTogglePassword,
        byId,
      ),
    [
      byId,
      handleTogglePassword,
      onDelete,
      onEdit,
      onViewMetadata,
      revealedPasswordIds,
    ],
  );
  const dataSource = useMemo(() => mapToRows(credentials), [credentials]);

  return (
    <ConfigListTable
      columns={columns}
      dataSource={dataSource}
      search={search}
      entityLabel="portal credentials"
      ariaLabel="Portal credentials table"
      setSearch={setSearch}
      setPage={setPage}
    />
  );
};
